import { Box } from '@mui/material'
import { useContext, useEffect, useRef, useState } from 'react'
import { SlideMenu } from '../components/SlideMenu'
import { SlideMenuLink } from '../components/SlideMenuLink'
import { useAuth } from '../hooks/useAuth'
import { useApiUrl } from '../hooks/useApiUrl'
import { ReactComponent as CotopatImage } from '../images/cotopat-logo.svg'
import './SideMenu.css'
import { ModalConfirmDialog } from '../components/ModalConfirmDialog'
import { loadStatus } from '../constants/loadStatus'
import { useCD01RhqInfoGet } from '../hooks/useCD01RhqInfoGet'
import { LanguageContext } from '../util/LanguageContext'

export const SideMenu: React.FC = () => {
  const auth = useAuth()
  const role = auth.role
  const rhq = auth.rhq
  const salesCompany = auth.salesCompany
  const menuRef = useRef<HTMLElement>(null)
  // 文字列変換用
  const translation = useContext(LanguageContext)
  const [privacyPolicyUrl, setPrivacyPolicyUrl] = useState('')
  const cd01 = useCD01RhqInfoGet()
  const [errorDialog, setErrorDialog] = useState('')

  // For DCP
  const isDcp = useApiUrl().isDcp

  useEffect(() => {
    auth.setIsDisable(cd01.loading === loadStatus.LOADING)
  }, [cd01.loading])

  useEffect(() => {
    const menu = menuRef.current
    const calculatePosition = () => {
      if (!menu) return
    }

    calculatePosition() // 初回レンダリング時にも計算を実行

    const handleResize = () => {
      calculatePosition() // リサイズ時に計算を実行
    }

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize) // コンポーネントがアンマウントされる時にイベントリスナーを削除
    }
  }, [])

  useEffect(() => {
    const fetchData = async () => {
      const rhqParam = rhq === 'KDEM' && salesCompany === 'TA' ? 'KDEM_TA' : rhq
      const result = await cd01.execRhqInfoGet(rhqParam)
      if (result.success && result.info) {
        const privacy_policy_url = result.info.privacy_policy_url
        setPrivacyPolicyUrl(privacy_policy_url)
      }
    }
    fetchData()
  }, [rhq, salesCompany])

  const handleDialogClose = () => {
    setErrorDialog('')
  }

  const hasError = () => errorDialog !== ''

  const handlePrivacyPolicyClick = (): boolean => {
    if (privacyPolicyUrl) {
      return true //Allow opening
    }
    setErrorDialog('privacy_policy_url_not_available')
    return false //Prevent opening
  }

  return (
    <>
      {hasError() && (
        <ModalConfirmDialog open={true} text={errorDialog} onClick={handleDialogClose} />
      )}
      <Box display="flex" flexDirection="column">
        <Box sx={{ mt: '30px', ml: '32px' }}>
          <CotopatImage width="172px" height="44px" />
        </Box>
        <Box>
          <aside id="menu" ref={menuRef}>
            <ul id="ul-top">
              {role === 1 /*1:窓口*/ && (
                <>
                  <SlideMenu
                    label={translation['label_priority_words']}
                    page="/phrase"
                    smallFont={false}
                  />
                  <SlideMenu
                    label={translation['label_emphasis_words']}
                    page="/word"
                    smallFont={false}
                  />
                  <SlideMenu
                    label={translation['label_forceword']}
                    page="/conversion"
                    smallFont={false}
                  />
                  <SlideMenu
                    label={translation['label_diagram']}
                    page="/illustration"
                    smallFont={false}
                  />
                  <SlideMenu
                    label={translation['label_logo']}
                    page="/logo"
                    smallFont={false}
                  />
                  <SlideMenu
                    label={translation['label_signage']}
                    page="/signage"
                    smallFont={false}
                  />
                  <SlideMenu
                    label={translation['button_import'] + '/' + translation['button_export']}
                    page="/importexport"
                    smallFont={false}
                  />
                  <SlideMenu
                    label={translation['label_conversation_history']}
                    page="/conversationlog"
                    smallFont={false}
                  />
                  {!isDcp && (
                    <>
                      <SlideMenu
                        label={translation['label_email']}
                        page="/email"
                        smallFont={false}
                      />
                      <SlideMenu
                        label={translation['label_password']}
                        page="/password"
                        smallFont={false}
                      />
                    </>
                  )}
                  <SlideMenuLink
                    label={translation['label_privacy_policy']}
                    url={privacyPolicyUrl}
                    smallFont={false}
                    onBeforeOpen={handlePrivacyPolicyClick}
                  />
                </>
              )}
              {role === 3 /*3:キッティング業者(RHQ Member/Kitting operator)*/ && (
                <>
                  <SlideMenu
                    label={translation['label_device_info_management']}
                    page="/device"
                    smallFont={false}
                  />
                </>
              )}
              {(role === 2 /*2:デバイス管理者(RHQ Administrator)*/ ||
                role === 4) /*4:京セラ社員*/ && (
                <>
                  <SlideMenu
                    label={translation['label_device_info_management']}
                    page="/device"
                    smallFont={false}
                  />
                  <SlideMenu
                    label={translation['label_admin_info']}
                    page="/user"
                    smallFont={false}
                  />
                </>
              )}
              {role === 0 /*root管理者*/ && (
                <>
                  <SlideMenu
                    label={translation['label_admin_info']}
                    page="/user"
                    smallFont={false}
                  />
                </>
              )}
              {role !== 0 && !isDcp && (
                <SlideMenu
                  label={translation['label_logout']}
                  page="/logout"
                  smallFont={false}
                />
              )}
            </ul>
          </aside>
        </Box>
      </Box>
    </>
  )
}
